<template>
  <NavBar pageName="IMSI Prices"></NavBar>
  <v-snackbar
      v-model="snackbarVisible"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
  >
    {{ snackbarMessage }}
    <template v-slot:action>
      <v-btn text color="red" @click="snackbarVisible = false">Close</v-btn>
    </template>
  </v-snackbar>

  <v-container name="IMSI Prices" style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings/imsi')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      IMSI Settings
    </v-btn>
    <v-card>
      <v-card-title>
        IMSI Prices Management
      </v-card-title>
      <v-card-subtitle>
        <v-btn
            color="primary"
            @click="addNewImsiPrice()"
        >
          Add New Price
        </v-btn>
        <v-btn
            class="ml-4"
            color="secondary"
            @click="goToSettings()"
        >
          Settings
        </v-btn>
      </v-card-subtitle>
      <v-card-text>
        <v-data-table
            :headers="headers"
            :items="filteredImsiPrices"
            item-value="id"
            class="elevation-1"
            :items-per-page="50"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Prices List</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-switch
                  v-model="showActiveOnly"
                  :label="showActiveOnly ? 'Active only' : 'All (incl. Terminated)'"
                  color="primary"
                  hide-details
                  density="compact"
                  class="mr-4"
              ></v-switch>
              <v-btn color="primary" @click="fetchImsiPrices">Refresh</v-btn>
            </v-toolbar>
          </template>

          <template v-slot:[`item.imsi_property_type`]="{ item }">
            <v-chip :color="getPropertyTypeColor(item.imsi_property_type)" small>
              {{ item.imsi_property_type?.toUpperCase() }}
            </v-chip>
          </template>

          <template v-slot:[`item.price_value`]="{ item }">
            {{ item.price_value }} {{ item.currency_name }}
          </template>

          <template v-slot:[`item.status_name`]="{ item }">
            <v-chip :color="item.status_name === 'active' ? 'success' : 'error'" small>
              {{ item.status_name }}
            </v-chip>
          </template>

          <template v-slot:[`item.action`]="{ item }">
            <template v-if="item.is_editable">
              <v-btn
                  color="primary"
                  size="small"
                  @click="editImsiPrice(item)"
              >
                Edit
              </v-btn>
              <v-btn
                  class="ml-2"
                  :color="item.status_name === 'active' ? 'warning' : 'success'"
                  size="small"
                  @click="toggleStatus(item)"
              >
                {{ item.status_name === 'active' ? 'Deactivate' : 'Activate' }}
              </v-btn>
            </template>
            <span v-else class="text-grey">View only</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="isEditing" max-width="600px">
      <v-card>
        <v-card-title>Edit IMSI Price</v-card-title>
        <v-card-text>
          <v-select
              v-model="selectedImsiPrice.managed_by_client_id"
              :items="manageableClients"
              item-title="name"
              item-value="id"
              label="Managed By Client"
              class="mb-2"
          ></v-select>
          <v-text-field
              v-model="selectedImsiPrice.name"
              label="Name"
          ></v-text-field>
          <v-textarea
              v-model="selectedImsiPrice.description"
              label="Description"
              rows="2"
          ></v-textarea>
          <v-select
              v-model="selectedImsiPrice.imsi_type_id"
              :items="imsiTypes"
              item-title="name"
              item-value="id"
              label="IMSI Type"
          ></v-select>
          <v-select
              v-model="selectedImsiPrice.imsi_property_type"
              :items="propertyTypes"
              label="Property Type"
          ></v-select>
          <v-select
              v-model="selectedImsiPrice.billing_period_id"
              :items="billingPeriods"
              item-title="name"
              item-value="id"
              label="Billing Period"
          ></v-select>
          <v-text-field
              v-model.number="selectedImsiPrice.price_value"
              label="Price Value"
              type="number"
              step="0.01"
          ></v-text-field>
          <v-select
              v-model="selectedImsiPrice.currency_id"
              :items="currencies"
              item-title="name"
              item-value="id"
              label="Currency"
          ></v-select>
          <v-select
              v-model="selectedImsiPrice.mvno_id"
              :items="mvnos"
              item-title="name"
              item-value="id"
              label="MVNO"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeEditor">Cancel</v-btn>
          <v-btn color="primary" @click="saveImsiPrice">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add New Dialog -->
    <v-dialog v-model="showAddNewImsiPrice" max-width="600px">
      <v-card>
        <v-card-title>Add New IMSI Price</v-card-title>
        <v-card-text>
          <v-select
              v-model="newImsiPrice.managed_by_client_id"
              :items="manageableClients"
              item-title="name"
              item-value="id"
              label="Managed By Client"
              class="mb-2"
          ></v-select>
          <v-text-field
              v-model="newImsiPrice.name"
              label="Name"
          ></v-text-field>
          <v-textarea
              v-model="newImsiPrice.description"
              label="Description"
              rows="2"
          ></v-textarea>
          <v-select
              v-model="newImsiPrice.imsi_type_id"
              :items="imsiTypes"
              item-title="name"
              item-value="id"
              label="IMSI Type"
          ></v-select>
          <v-select
              v-model="newImsiPrice.imsi_property_type"
              :items="propertyTypes"
              label="Property Type"
          ></v-select>
          <v-select
              v-model="newImsiPrice.billing_period_id"
              :items="billingPeriods"
              item-title="name"
              item-value="id"
              label="Billing Period"
          ></v-select>
          <v-text-field
              v-model.number="newImsiPrice.price_value"
              label="Price Value"
              type="number"
              step="0.01"
          ></v-text-field>
          <v-select
              v-model="newImsiPrice.currency_id"
              :items="currencies"
              item-title="name"
              item-value="id"
              label="Currency"
          ></v-select>
          <v-select
              v-model="newImsiPrice.mvno_id"
              :items="mvnos"
              item-title="name"
              item-value="id"
              label="MVNO"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeAddDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveNewImsiPrice">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { log } from "@/composables/useLog";
import { getImsiPricesList, addImsiPrice, updateImsiPrice } from "@/composables/useGetImsiPricesAPI";

export default {
  name: 'ImsiPricesSettings',
  components: { NavBar },
  data() {
    return {
      token: '',
      imsiPrices: [],
      imsiTypes: [],
      billingPeriods: [],
      currencies: [],
      mvnos: [],
      imsiPriceStatuses: [],
      manageableClients: [],
      currentClient: null,
      selectedImsiPrice: {},
      isEditing: false,
      showAddNewImsiPrice: false,
      newImsiPrice: {
        name: '',
        description: '',
        imsi_type_id: null,
        imsi_property_type: 'buy',
        billing_period_id: null,
        price_value: 0,
        currency_id: null,
        mvno_id: null,
        managed_by_client_id: null
      },
      propertyTypes: ['buy', 'rent', 'transfer'],
      headers: [
        { title: "ID", key: "id", width: "60px" },
        { title: "Managed By", key: "managed_by_client_name" },
        { title: "Name", key: "name" },
        { title: "IMSI Type", key: "imsi_type_name" },
        { title: "Type", key: "imsi_property_type" },
        { title: "Billing Period", key: "billing_period_name" },
        { title: "Price", key: "price_value" },
        { title: "MVNO", key: "mvno_name" },
        { title: "Status", key: "status_name" },
        { title: "Actions", key: "action", sortable: false, width: "200px" },
      ],
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 2000,
      showActiveOnly: true,
    };
  },
  computed: {
    filteredImsiPrices() {
      if (this.showActiveOnly) {
        return this.imsiPrices.filter(p => p.status_name === 'active');
      }
      return this.imsiPrices;
    }
  },
  methods: {
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarVisible = true;
    },
    getPropertyTypeColor(type) {
      switch (type) {
        case 'buy': return 'success';
        case 'rent': return 'info';
        case 'transfer': return 'warning';
        default: return 'grey';
      }
    },
    goToSettings() {
      this.$router.replace({ name: "settings" });
    },
    addNewImsiPrice() {
      this.newImsiPrice = {
        name: '',
        description: '',
        imsi_type_id: this.imsiTypes[0]?.id || null,
        imsi_property_type: 'buy',
        billing_period_id: this.billingPeriods[0]?.id || null,
        price_value: 0,
        currency_id: this.currencies[0]?.id || null,
        mvno_id: this.mvnos[0]?.id || null,
        managed_by_client_id: this.currentClient?.id || null
      };
      this.showAddNewImsiPrice = true;
    },
    async fetchImsiPrices() {
      log("Fetching IMSI Prices...");
      const response = await getImsiPricesList(this.token);
      if (response.error) {
        this.showSnackbar("Error fetching prices: " + response.message, "error");
      } else {
        this.imsiPrices = response.imsi_prices || [];
        this.imsiTypes = response.imsi_types || [];
        this.billingPeriods = response.billing_periods || [];
        this.currencies = response.currencies || [];
        this.mvnos = response.mvnos || [];
        this.imsiPriceStatuses = response.imsi_price_statuses || [];
        this.manageableClients = response.manageable_clients || [];
        this.currentClient = response.current_client;
        log("Loaded " + this.imsiPrices.length + " prices");
        log("Manageable clients: " + this.manageableClients.length);
      }
    },
    editImsiPrice(item) {
      this.selectedImsiPrice = { ...item };
      this.isEditing = true;
    },
    async saveImsiPrice() {
      const response = await updateImsiPrice(this.token, this.selectedImsiPrice);
      if (response.error) {
        this.showSnackbar("Error updating price: " + response.message, "error");
      } else {
        this.showSnackbar("Price updated successfully", "success");
        this.closeEditor();
        this.fetchImsiPrices();
      }
    },
    async saveNewImsiPrice() {
      const response = await addImsiPrice(this.token, this.newImsiPrice);
      if (response.error) {
        this.showSnackbar("Error adding price: " + response.message, "error");
      } else {
        this.showSnackbar("Price added successfully", "success");
        this.closeAddDialog();
        this.fetchImsiPrices();
      }
    },
    async toggleStatus(item) {
      const activeStatus = this.imsiPriceStatuses.find(s => s.name === 'active');
      const terminatedStatus = this.imsiPriceStatuses.find(s => s.name === 'terminated');

      if (!activeStatus || !terminatedStatus) {
        this.showSnackbar("Status types not found", "error");
        return;
      }

      const newStatusId = item.status_name === 'active' ? terminatedStatus.id : activeStatus.id;

      const response = await updateImsiPrice(this.token, {
        id: item.id,
        imsi_price_status_id: newStatusId
      });

      if (response.error) {
        this.showSnackbar("Error updating status: " + response.message, "error");
      } else {
        this.showSnackbar("Status updated successfully", "success");
        this.fetchImsiPrices();
      }
    },
    closeEditor() {
      this.isEditing = false;
      this.selectedImsiPrice = {};
    },
    closeAddDialog() {
      this.showAddNewImsiPrice = false;
    },
  },
  created() {
    const { getLoggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
      log("ImsiPricesSettings created");
      log("token: " + token.value);
      this.token = token.value;
      this.fetchImsiPrices();
    });
  },
};
</script>
